import { useEffect, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import {
  AccountingBpView,
  buildAccountingBpSapHtml,
} from '@/components/Approval/@components/AccountingBusinessPartner';
import { getAccountingBpDraftSourceUrl } from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  ACCOUNTING_BP_LOAD_ERROR_MESSAGE,
  ACCOUNTING_BP_MISSING_MESSAGE,
  ACCOUNTING_BP_TYPE,
  buildAccountingBpSubject,
  createAccountingBpState,
  toAccountingBpPartners,
  withAccountingBpRequestType,
  type AccountingBpDraftSource,
} from '@/utils/approval/accountingBusinessPartner';
import { createBpState } from '@/utils/approval/businessPartnerRegistration';
import BusinessPartnerRegistrationField from './BusinessPartnerRegistrationField';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

type AccountingBusinessPartnerFieldProps = {
  /** 신규는 양식 응답, 편집은 저장값 복원이 끝난 뒤 true — 그때 묶음번호로 1회 조회 */
  ready: boolean;
};

/**
 * 회계거래처 신규/변경(101) — SAP 묶음번호로 회사명·거래처 표를 그리고, 마지막 행이 '생성'이면 업체소싱정보 입력.
 * 편집으로 다시 열어도 다시 조회하고 제목도 덮어씀(레거시 동일)
 */
const AccountingBusinessPartnerField = ({
  ready,
}: AccountingBusinessPartnerFieldProps) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'accountingBp' });
  const form = useWatch({ control, name: 'bpRegistration' });
  const startedRef = useRef(false);
  const aliveRef = useRef(true);

  useEffect(
    () => () => {
      aliveRef.current = false;
    },
    [],
  );

  useEffect(() => {
    if (!ready || startedRef.current) return;
    startedRef.current = true;
    const sapDocumentNumber =
      getValues('accountingBp')?.sapDocumentNumber ?? null;
    if (!sapDocumentNumber) {
      snackbar.showError({ message: t(ACCOUNTING_BP_MISSING_MESSAGE) });
      return;
    }
    setValue('accountingBp', {
      ...createAccountingBpState(sapDocumentNumber),
      loading: true,
    });
    getNestedDataFetcher<AccountingBpDraftSource>(
      getAccountingBpDraftSourceUrl(sapDocumentNumber),
    )
      .then((res) => {
        if (!aliveRef.current) return;
        const messages = (res.display?.messages ?? []).filter(Boolean);
        if (messages.length) {
          setValue('accountingBp', createAccountingBpState(sapDocumentNumber));
          messages.forEach((message) => snackbar.showError({ message }));
          snackbar.showError({ message: t(ACCOUNTING_BP_LOAD_ERROR_MESSAGE) });
          return;
        }
        const partners = toAccountingBpPartners(res.display?.partners);
        const autoSubject = buildAccountingBpSubject(partners, t);
        setValue('accountingBp', {
          ...createAccountingBpState(sapDocumentNumber),
          companyName: res.display?.companyName?.trim() ?? '',
          partners,
          ...(autoSubject !== null && { autoSubject }),
        });
        setValue(
          'bpRegistration',
          withAccountingBpRequestType(
            getValues('bpRegistration') ?? createBpState(),
            partners,
          ),
        );
        if (autoSubject !== null) {
          setValue('subject', autoSubject, { shouldDirty: false });
        }
      })
      .catch(() => {
        if (!aliveRef.current) return;
        setValue('accountingBp', createAccountingBpState(sapDocumentNumber));
        snackbar.showError({ message: t(ACCOUNTING_BP_MISSING_MESSAGE) });
      });
  }, [ready, getValues, setValue, snackbar, t]);

  const sapHtml = useMemo(
    () =>
      buildAccountingBpSapHtml(
        {
          companyName: state?.companyName ?? '',
          partners: state?.partners ?? [],
        },
        t,
      ),
    [state?.companyName, state?.partners, t],
  );

  return (
    <>
      <FieldBlock className="accounting-bp">
        {state?.loading && (
          <ApprovalNoticeBox role="status" sx={{ mb: 1 }}>
            {t('데이터를 불러오는 중입니다...')}
          </ApprovalNoticeBox>
        )}
        <AccountingBpView content={sapHtml} />
      </FieldBlock>
      {form?.requestType === 'register' && !!state?.partners.length && (
        <BusinessPartnerRegistrationField
          apvType={ACCOUNTING_BP_TYPE}
          hideRequestType
        />
      )}
    </>
  );
};

export default AccountingBusinessPartnerField;
